import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  message,
  DatePicker,
  Space,
  Typography,
  Row,
  Col,
  Select,
  Input,
  Table,
  Tag,
  Drawer,
} from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { formatDateTimeCustomize } from "../../../utils/dateTime";
import { actionsHelpers, logAPIWithErrorHandling } from "../../../api/logs/log";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, spacing } from "../../../theme/tokens";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["user_id", "description", "created_at"];

const LogActionScreen = () => {
  const [loading, setLoading] = useState(false);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [logData, setLogData] = useState([]);
  const [actions, setActions] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 30,
    total: 0,
  });

  // Filter state
  const [actionFilter, setActionFilter] = useState(undefined);
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);

  // Audit detail state
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const { user } = useSelector((state) => state.persistedReducer.user);
  const isAdmin = user?.data?.info?.type === "ADMIN";

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const fetchActions = async () => {
    try {
      setActionsLoading(true);
      const response = await logAPIWithErrorHandling.getActions();
      if (response.data.success) {
        setActions(response.data.data);
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setActionsLoading(false);
    }
  };

  const fetchLogs = async (params = {}) => {
    setLoading(true);
    try {
      const paramDatas = {
        page: params.page || pagination.current,
        action: params.action,
        search: params.search,
        from_date: params.from_date,
        to_date: params.to_date,
        is_read: params.is_read,
      };

      const response = await logAPIWithErrorHandling.getLogActions(paramDatas);
      const { actioLogs: collectionData, pagination: paginationData } =
        actionsHelpers.extractActionList(response);

      setLogData(collectionData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
        from: paginationData.from,
        to: paginationData.to,
        lastPage: paginationData.lastPage,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách lịch sử!");
      console.error("Error fetching histories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActions();
    const today = dayjs().format("YYYY-MM-DD");

    setDateRange([dayjs(), dayjs()]);
    setStatusFilter(isAdmin ? undefined : 0); // Mặc định hiện chưa xem cho SHOP/STAFF

    fetchLogs({
      from_date: today,
      to_date: today,
      is_read: isAdmin ? "" : 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Xây dựng tham số lọc từ state hiện tại + override
  const buildParams = (over = {}) => {
    const action = "action" in over ? over.action : actionFilter;
    const search = "search" in over ? over.search : searchFilter;
    const status = "status" in over ? over.status : statusFilter;
    const range = "dateRange" in over ? over.dateRange : dateRange;

    const params = { page: over.page || 1 };
    if (action) params.action = action;
    if (search) params.search = search.trim();
    if (status !== undefined) params.is_read = status;
    if (range && range.length === 2 && range[0] && range[1]) {
      params.from_date = dayjs(range[0]).format("YYYY-MM-DD");
      params.to_date = dayjs(range[1]).format("YYYY-MM-DD");
    }
    return params;
  };

  const applyFilters = (over = {}) => {
    fetchLogs(buildParams(over));
  };

  const handleRefresh = () => {
    applyFilters({ page: 1 });
  };

  const handleActionChange = (value) => {
    setActionFilter(value);
    applyFilters({ action: value, page: 1 });
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    applyFilters({ status: value, page: 1 });
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    applyFilters({ dateRange: dates, page: 1 });
  };

  const handleTableChange = (paginationInfo) => {
    fetchLogs(buildParams({ page: paginationInfo.current }));
  };

  // Inline Mark as read
  const handleMarkAsRead = async (record) => {
    try {
      await logAPIWithErrorHandling.markAsRead([record.id]);
      message.success("Đã đánh dấu là đã xem");
      setLogData((prev) =>
        prev.map((item) =>
          item.id === record.id ? { ...item, is_read: true } : item
        )
      );
    } catch (err) {
      message.error("Thao tác thất bại");
    }
  };

  // View audit detail
  const handleViewDetail = async (record) => {
    setSelectedLog(record);
    setDetailVisible(true);

    // If unread and NOT admin, mark as read
    if (!record.is_read && !isAdmin) {
      try {
        await logAPIWithErrorHandling.markAsRead([record.id]);
        // Update local state without re-fetching
        setLogData((prev) =>
          prev.map((item) =>
            item.id === record.id ? { ...item, is_read: true } : item
          )
        );
      } catch (err) {
        console.error("Failed to mark as read", err);
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setLoading(true);
      await logAPIWithErrorHandling.markAllAsRead();
      message.success("Đã đánh dấu tất cả là đã xem!");
      handleRefresh();
    } catch (error) {
      message.error("Thao tác thất bại!");
    } finally {
      setLoading(false);
    }
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "id", label: "ID", fixed: true,
      column: {
        title: "ID", dataIndex: "id", key: "id", width: 200, align: "center",
        render: (id, record) => (
          <Space size={4}>
            <Text>{id}</Text>
            {!record.is_read && (
              <Tag color="#E5065C" className="new-log-tag" style={{ margin: 0, fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>NEW</Tag>
            )}
          </Space>
        ),
      },
    },
    {
      key: "user_id", label: "Người dùng",
      column: {
        title: "Người dùng", dataIndex: "user_id", key: "user_id", width: 150,
        render: (userId, record) => (
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: '13px' }}>
              <UserOutlined style={{ marginRight: 4 }} />
              {record?.user?.username ?? record?.user?.email ?? "System"}
            </Text>
            {record.auth_user_id && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                Partner ID: {record.partner_id}
              </Text>
            )}
          </Space>
        ),
      },
    },
    {
      key: "description", label: "Hành động",
      column: {
        title: "Hành động", dataIndex: "description", key: "description", width: 350,
        render: (text) => (
          <Text style={{ fontSize: '13px' }}>{text}</Text>
        ),
      },
    },
    actionColumnDef(
      (record) => [
        {
          key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view",
          hidden: !record.changed_fields,
          onClick: () => handleViewDetail(record),
        },
        {
          key: "read", label: "Đánh dấu đã xem", icon: <CheckCircleOutlined />, tone: "success",
          hidden: !(!record.is_read && !isAdmin),
          onClick: () => handleMarkAsRead(record),
        },
      ],
      { width: 100 }
    ),
    {
      key: "created_at", label: "Thời gian",
      column: {
        title: "Thời gian", dataIndex: "created_at", key: "created_at", width: 160,
        render: (time) => (
          <Text type="secondary" style={{ fontSize: '13px' }}>
            {formatDateTimeCustomize(time)}
          </Text>
        ),
      },
    },
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "action", label: "Hành động", span: { xs: 24, sm: 12, md: 6 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Chọn loại"
          allowClear
          loading={actionsLoading}
          showSearch
          optionFilterProp="children"
          value={actionFilter}
          onChange={handleActionChange}
        >
          {Object.entries(actions).map(([key, value]) => (
            <Option key={key} value={key}>
              {value}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 6 },
      field: (
        <Input
          placeholder="ID bản ghi, tên..."
          allowClear
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          onPressEnter={() => applyFilters({ page: 1 })}
        />
      ),
    },
    ...(!isAdmin
      ? [
          {
            key: "status", label: "Trạng thái", span: { xs: 24, sm: 12, md: 6 },
            field: (
              <Select
                style={{ width: "100%" }}
                placeholder="Tất cả"
                allowClear
                value={statusFilter}
                onChange={handleStatusChange}
              >
                <Option value={0}>Chưa xem</Option>
                <Option value={1}>Đã xem</Option>
              </Select>
            ),
          },
        ]
      : []),
    {
      key: "dateRange", label: "Khoảng thời gian", span: { xs: 24, sm: 12, md: 6 },
      field: (
        <RangePicker
          style={{ width: "100%" }}
          format="DD/MM/YYYY"
          value={dateRange}
          onChange={handleDateChange}
          ranges={{
            'Hôm nay': [dayjs(), dayjs()],
            '7 ngày qua': [dayjs().subtract(7, 'day'), dayjs()],
            'Tháng này': [dayjs().startOf('month'), dayjs().endOf('month')],
          }}
        />
      ),
    },
  ];

  // Helper render for JSON diff
  const renderAuditDiff = (changedFields) => {
    if (!changedFields) return null;

    const dataSource = Object.entries(changedFields).map(([field, values]) => ({
      key: field,
      field: values.label || field,
      old: values.old,
      new: values.new,
    }));

    const renderValue = (val) => {
      if (val === null || val === undefined || val === '') return <Text type="secondary" italic>(Trống)</Text>;
      if (val === true || val === 1 || val === '1') return <Tag color="green">Bật / Có / Đã xong</Tag>;
      if (val === false || val === 0 || val === '0') return <Tag color="red">Tắt / Không / Chưa</Tag>;
      return String(val);
    };

    const auditColumns = [
      {
        title: "Trường thay đổi",
        dataIndex: "field",
        key: "field",
        width: "30%",
        render: (text) => <Text strong style={{ color: '#475569' }}>{text}</Text>,
      },
      {
        title: "Giá trị cũ",
        dataIndex: "old",
        key: "old",
        width: "35%",
        render: (val) => (
          <div style={{ backgroundColor: "#FFF0F4", padding: "6px 10px", borderRadius: 6, border: '1px solid #FFB8C9', color: '#E5065C' }}>
            {renderValue(val)}
          </div>
        ),
      },
      {
        title: "Giá trị mới",
        dataIndex: "new",
        key: "new",
        width: "35%",
        render: (val) => (
          <div style={{ backgroundColor: "#EAF9F2", padding: "6px 10px", borderRadius: 6, border: '1px solid #B8E8D0', color: '#128555', fontWeight: 'bold' }}>
            {renderValue(val)}
          </div>
        ),
      },
    ];

    return (
      <Table
        columns={auditColumns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        bordered
      />
    );
  };

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(
    actionFilter ||
    searchFilter ||
    statusFilter !== undefined ||
    (dateRange && dateRange[0] && dateRange[1])
  );

  const handleClearFilter = () => {
    setActionFilter(undefined);
    setSearchFilter("");
    setStatusFilter(isAdmin ? undefined : 0);
    const today = dayjs();
    setDateRange([today, today]);
    fetchLogs({
      page: 1,
      from_date: today.format("YYYY-MM-DD"),
      to_date: today.format("YYYY-MM-DD"),
      is_read: isAdmin ? "" : 0,
    });
  };

  return (
    <BrandedPageLayout
      title="Nhật ký thao tác"
      subtitle="Theo dõi và quản lý nhật ký hoạt động (Audit Trails)."
      breadcrumbItems={[{ title: "Nhật ký thao tác" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleClearFilter}
        onReload={handleRefresh}
        extra={
          !isAdmin && (
            <Button
              icon={<CheckCircleOutlined />}
              onClick={handleMarkAllRead}
              disabled={logData.every((item) => item.is_read)}
            >
              Đánh dấu đã đọc tất cả
            </Button>
          )
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={logData}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
        rowClassName={(record) => (!record.is_read ? "unread-log-row" : "")}
        className="audit-log-table"
        emptyText="Không có dữ liệu"
      />

      {/* Audit Detail Drawer */}
      <Drawer
        title={
          <Space>
            <InfoCircleOutlined style={{ color: '#0070D7' }} />
            <span>Chi tiết thay đổi dữ liệu</span>
          </Space>
        }
        placement="right"
        width="min(880px, 100vw)"
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button onClick={() => setDetailVisible(false)}>Đóng</Button>
          </div>
        }
      >
        {selectedLog && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">Mô tả: </Text>
                <Text strong>{selectedLog.description}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Thời gian: </Text>
                <Text>{formatDateTimeCustomize(selectedLog.created_at)}</Text>
              </Col>
            </Row>

            <div style={{ marginTop: 8 }}>
              <Text type="secondary" block style={{ marginBottom: 10 }}>Dữ liệu thay đổi chi tiết:</Text>
              {renderAuditDiff(selectedLog.changed_fields)}
            </div>

            <Row gutter={16} style={{ backgroundColor: '#F8FAFC', padding: '12px', borderRadius: 4 }}>
              <Col span={8}>
                <Text type="secondary" block style={{ fontSize: '12px' }}>Địa chỉ IP</Text>
                <Text>{selectedLog.ip_address}</Text>
              </Col>
              <Col span={16}>
                <Text type="secondary" block style={{ fontSize: '12px' }}>Thiết bị/Browser</Text>
                <Text style={{ fontSize: '12px' }}>{selectedLog.user_agent}</Text>
              </Col>
            </Row>
          </Space>
        )}
      </Drawer>
    </BrandedPageLayout>
  );
};

export default LogActionScreen;
